import { useId } from "vue";

/** A stable segment used to compose a component-scoped DOM identifier. */
export type CompositeIdPart = string | number | null | undefined;

/** Values returned by {@link useCompositeId}. */
export type UseCompositeIdReturn = {
  /** Unique Vue component-instance identifier, prefixed for FeatherK markup. */
  instanceId: string;
  /** Combines the component instance ID with stable logical identifier segments. */
  compose: (...parts: CompositeIdPart[]) => string;
};

/**
 * Creates component-scoped DOM identifiers from Vue's SSR-safe `useId()`.
 *
 * Use the resulting `instanceId` for a stable local base and `compose` for
 * related trigger, menu, popup, and ARIA relationship IDs. This does not
 * replace consumer-owned domain IDs used for shared state or registries.
 */
export const useCompositeId = (prefix = "fkid"): UseCompositeIdReturn => {
  const instanceId = `${prefix}-${useId()}`;

  const compose = (...parts: CompositeIdPart[]) =>
    [instanceId, ...parts]
      .filter((part) => part !== null && part !== undefined)
      .map(String)
      .join("-");

  return { instanceId, compose };
};
